import { Injectable } from '@angular/core';
import moment from 'moment';
import { ChartInterval, ChartType } from '@ui/shared/model';

@Injectable({
  providedIn: 'root'
})
export class ChartJsService {
  public _getTimeUnit(interval: string) {
    switch (interval) {
      case ChartInterval.HOUR:
        return 'hour';
      case ChartInterval.DAY:
        return 'day';
      case ChartInterval.WEEK:
        return 'week';
      case ChartInterval.MONTH:
        return 'month';
      case ChartInterval.QUARTER:
        return 'quarter';
      case ChartInterval.YEAR:
        return 'year';
      default:
        return false;
    }
  }

  public _getYAxisStepSize(value) {
    if (value > 1000000) {
      return 100000;
    } else if (value > 100000) {
      return 10000;
    } else if (value > 10000) {
      return 1000;
    } else if (value > 3000) {
      return 500;
    } else if (value > 1000) {
      return 100;
    } else if (value > 100) {
      return 50;
    } else if (value > 10) {
      return 10;
    }
    return 1;
  }

  public getChartConfig(
    chartType: string,
    interval: string,
    yMaxValue: number,
    hideLegend: boolean
  ) {
    const yStepSize = this._getYAxisStepSize(yMaxValue);
    const unit = this._getTimeUnit(interval);
    return {
      options: {
        animation: {
          duration: 300, // general animation time
          easing: 'easeOutQuart'
        },
        hover: {
          animationDuration: 300 // duration of animations when hovering an item
        },

        responsive: true,
        maintainAspectRatio: false, // required for resizing (responsiveness)
        aspectRatio: 1,
        showLines: true,
        tooltips: {
          cornerRadius: 0,
          displayColors: false,
          enabled: true,
          mode: 'nearest',
          intersect: true,
          callbacks: {
            title: tooltipItems => {
              if (chartType === ChartType.PIE) {
                return '';
              }
              const format =
                interval === ChartInterval.HOUR
                  ? 'D. MMM YYYY, HH:mm.ss [Uhr]'
                  : 'D. MMM YYYY';
              const date = moment(tooltipItems[0].xLabel);
              return date.isValid() ? date.locale('de').format(format) : '';
            },
            label: (tooltipItems, data) => {
              let text: string, number;
              if (chartType === ChartType.PIE) {
                const index = Array.isArray(tooltipItems)
                  ? tooltipItems[0].index
                  : tooltipItems.index;
                text = data.labels[index];
                number = data.datasets[0].data[index];
              } else {
                text = data.datasets[tooltipItems.datasetIndex]
                  ? data.datasets[tooltipItems.datasetIndex].label
                  : '';
                number = tooltipItems.yLabel;
              }
              return `${text}: ${String(number)}`;
            }
          }
        },
        legend: {
          display: !hideLegend
        },
        elements: {
          line: {
            tension: 0 // disables bezier curves
          }
        },
        scales: {
          xAxes: [
            {
              id: 'x-axis-0',
              type: 'time',
              display: chartType !== ChartType.PIE, // hide for Pie chart
              stacked: chartType === ChartType.BAR, // only stack Bar chart
              offset: chartType === ChartType.BAR, // line chart should start at zero
              distribution: 'series', // default 'linear' -- shows dates since 1970 (?)
              bounds: 'data', // default 'data'
              ticks: {
                source: 'auto',
                padding: 5,
                autoSkip: true,
                autoSkipPadding: 50, // cleans up X-axis for large data sets / skips more labels
                maxRotation: 0, // actual rotation of the label (angle) if labels to not fit
                minRotation: 0,
                callback: function (value) {
                  const displayFormats = {
                    minute: 'D. MMM HH:mm',
                    hour: 'D. MMM HH:mm',
                    day: 'D. MMM',
                    week: '[KW] w (D. MMM)',
                    month: 'MMM YYYY',
                    quarter: '[Q]Q - YYYY',
                    year: 'YYYY'
                  };
                  const unitTmp = unit || 'week';
                  const format = displayFormats[unitTmp];
                  return moment(value, 'DD-MM-YYYY HH:mm')
                    .locale('de')
                    .format(format);
                }
              },
              time: {
                isoWeekday: true,
                unit: unit,
                minUnit: 'hour',
                // have the same formats for all units, so we can parse the date in the custom formatter
                // in property ticks.callback reason is so we can translate the x-axis labels using momentjs
                displayFormats: {
                  minute: 'DD-MM-YYYY HH:mm',
                  hour: 'DD-MM-YYYY HH:mm',
                  day: 'DD-MM-YYYY HH:mm',
                  week: 'DD-MM-YYYY HH:mm',
                  month: 'DD-MM-YYYY HH:mm',
                  quarter: 'DD-MM-YYYY HH:mm',
                  year: 'DD-MM-YYYY HH:mm'
                }
              },
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              id: 'y-axis-0',
              display: chartType !== ChartType.PIE, // hide for Pie chart
              stacked: chartType === ChartType.BAR, // only stack Bar chart
              gridLines: {
                display: true
              },
              ticks: {
                stepSize: yStepSize,
                beginAtZero: true
              }
            }
          ]
        }
      }
    };
  }
}
